import { useState, useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Input } from "@/components/ui/input";
import { MapPin, Loader2 } from "lucide-react";

/**
 * Italian city autocomplete using /api/utils/cities backed by codicefiscale lib.
 * value/onChange manage the city name. onSelect receives full {name, province, code}.
 */
export function CityAutocomplete({ value, onChange, onSelect, placeholder, testid, publicMode = false, inputClassName = "" }) {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);
  // True solo se l'utente ha effettivamente digitato/modificato il campo in questa sessione.
  // Impedisce che il dropdown si apra automaticamente al focus/hover quando il campo è già valorizzato (es. modifica cliente esistente).
  const interactedRef = useRef(false);

  useEffect(() => {
    const onDocClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    const q = (value || "").trim();
    if (q.length < 2) { setResults([]); setOpen(false); return; }
    // Se l'utente non ha ancora digitato (campo precompilato), non eseguiamo nemmeno la ricerca
    if (!interactedRef.current) return;
    setLoading(true);
    const ctl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const endpoint = publicMode ? "/public/cities" : "/utils/cities";
        const { data } = await api.get(endpoint, { params: { q }, signal: ctl.signal });
        setResults(data);
        setOpen(data.length > 0);
        setHighlight(0);
      } catch (e) { logger.error("City autocomplete failed:", e); }
      finally { setLoading(false); }
    }, 120);
    return () => { ctl.abort(); clearTimeout(t); };
  }, [value, publicMode]);

  const pick = (item) => {
    onChange(item.name);
    onSelect?.(item);
    setOpen(false);
    // Dopo la selezione il campo è "stabile": resetto interacted così un re-focus non riapre il dropdown
    interactedRef.current = false;
  };

  const onKey = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(h + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); pick(results[highlight]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div className="relative" ref={wrapRef}>
      <Input
        data-testid={testid}
        value={value || ""}
        onChange={(e) => { interactedRef.current = true; onChange(e.target.value); }}
        onFocus={() => { if (interactedRef.current && results.length > 0) setOpen(true); }}
        onKeyDown={onKey}
        placeholder={placeholder || "Inizia a digitare un comune…"}
        autoComplete="off"
        className={inputClassName}
      />
      {loading && <Loader2 className="absolute right-2.5 top-2.5 h-4 w-4 animate-spin text-slate-400" />}
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-72 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg scrollbar-thin" data-testid={`${testid}-list`}>
          {results.map((r, i) => (
            <button
              key={`${r.code}-${r.name}`}
              type="button"
              onMouseEnter={() => setHighlight(i)}
              onClick={() => pick(r)}
              className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${i === highlight ? "bg-blue-50" : "hover:bg-slate-50"}`}
            >
              <MapPin className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
              <span className="font-medium flex-1 truncate">{r.name}</span>
              <span className="text-xs text-slate-500 bg-slate-100 rounded px-1.5 py-0.5 font-mono">{r.province}</span>
              {r.cap && <span className="text-xs text-slate-400 tabular-nums">{r.cap}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
